<template>
  <!--
    AB#21807 — the team grid on the Needs Attention team overview: one row per
    scored person, a face per day for the last 7 days, then the last 7 and the last 30 days.
    Only for view-team-communication-mood. Opening a row's drawer is logged
    server-side, and the note under the grid says so.
  -->
  <section class="tm" data-test="team-mood-grid" aria-label="Team comms mood">
    <div class="tm-scroll">
      <table class="tm-table">
        <thead>
          <tr>
            <th class="tm-who-h" scope="col">{{ personLabel }}</th>
            <th v-for="d in team.days" :key="d" scope="col" :title="d">{{ dayName(d) }}</th>
            <th class="tm-wk" scope="col">Week</th>
            <th scope="col">Month</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in team.people" :key="p.user_id" data-test="team-mood-row">
            <td>
              <button
                type="button"
                class="tm-who"
                :title="`Open ${p.name}'s comms mood`"
                @click="$emit('open', p)"
              >
                <span class="tm-av" aria-hidden="true">{{ initials(p.name) }}</span>
                <span class="tm-name">{{ p.name }}</span>
              </button>
            </td>
            <td v-for="(l, i) in p.days" :key="i">
              <MoodFace v-if="l !== null" :level="l" :size="24" />
              <span v-else class="tm-future" aria-label="Still to come">·</span>
            </td>
            <td class="tm-wk"><MoodFace :level="p.week" :size="28" /></td>
            <td><MoodFace :level="p.month" :size="28" /></td>
          </tr>
          <tr v-if="!team.people.length">
            <td :colspan="team.days.length + 3" class="tm-empty">
              Nobody is scored yet — give a role one of the two Comms mood permissions first.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="tm-note">
      A week needs a pattern: one bad day never makes a bad week. Opening someone's detail is
      recorded.
    </p>
  </section>
</template>

<script setup lang="ts">
import MoodFace from './MoodFace.vue';
import type { TeamMood, TeamMoodPerson } from '@/composables/useCommsMood';

withDefaults(
  defineProps<{
    team: TeamMood;
    /** AB#22134 — the column's name: "Order admin" for the order admin team, "CS Manager" for the CSM team. */
    personLabel?: string;
  }>(),
  { personLabel: 'Order admin' }
);
defineEmits<{ (e: 'open', person: TeamMoodPerson): void }>();

// The columns are the last 7 days, today last — so the weekday comes from the date.
const dayName = (d: string): string =>
  new Date(`${d}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' });

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
</script>

<style scoped>
.tm {
  /* Same column as the rest of the Needs Attention page. */
  max-width: 900px;
  margin: 0 auto;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  /* clip, not hidden: rounds the corners without becoming a scroll container,
     which would trap the sticky header below (AB#21900). */
  overflow: clip;
}
.tm-scroll {
  /* Wide enough for the whole grid: no scroll container here either, so the
     header sticks to the drawer's scrolling body. Narrow screens keep the
     sideways scroll (and a non-sticky header) rather than cut columns off. */
  overflow-x: clip;
}
@media (max-width: 760px) {
  .tm-scroll {
    overflow-x: auto;
  }
}
.tm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.tm-table th {
  padding: 10px 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ink-mute);
  text-align: center;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
  /* AB#21900 — the day/week/month headings stay in view while the list scrolls.
     The shadow stands in for the bottom border, which a sticky cell loses. */
  position: sticky;
  /* The scroll area's own top padding would otherwise show rows sliding by
     above the header; the host says how much padding to cover. */
  top: var(--tm-sticky-top, 0px);
  z-index: 2;
  box-shadow: inset 0 -1px 0 var(--border);
}
.tm-table th.tm-who-h {
  text-align: left;
  padding-left: 16px;
}
.tm-table td {
  padding: 8px;
  text-align: center;
  border-bottom: 1px solid var(--border-soft);
}
.tm-table td:first-child {
  text-align: left;
  padding-left: 8px;
}
.tm-table tbody tr:last-child td {
  border-bottom: 0;
}
.tm-table tbody tr:hover td {
  background: var(--surface-2);
}
.tm-wk {
  border-left: 1px solid var(--border-soft);
}
.tm-who {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 2px 8px;
  border: 0;
  background: transparent;
  color: var(--ink);
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
}
.tm-who:hover .tm-name {
  color: var(--accent);
  text-decoration: underline;
}
.tm-who:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: 6px;
}
.tm-av {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 11px;
  font-weight: 800;
}
.tm-future {
  color: var(--ink-mute);
}
.tm-empty {
  padding: 18px !important;
  color: var(--ink-soft);
}
.tm-note {
  margin: 0;
  padding: 10px 16px;
  font-size: 12px;
  color: var(--ink-mute);
  border-top: 1px solid var(--border-soft);
}
</style>
